import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import {
  MatLegacyPaginator as MatPaginator,
  LegacyPageEvent as PageEvent,
} from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';

import { Store } from '@ngrx/store';
import {
  Observable,
  debounceTime,
  distinctUntilChanged,
  map,
  skip,
} from 'rxjs';
import { SubSink } from 'subsink';

import { PageMerchantSearchResponse } from '@app/api/models';
import {
  MerchantActions,
  MerchantSearchRequest,
  MerchantSelectors,
} from '@merchant/store/merchant.slice';
import { TableDataSource } from '@shared/components/table/table-data-source.model';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';

@Component({
  selector: 'app-partner-search-modal',
  templateUrl: './partner-search-modal.component.html',
  styleUrls: ['./partner-search-modal.component.scss'],
})
export class PartnerSearchModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  partners$ = this.store
    .select(MerchantSelectors.selectPartnersResponse)
    .pipe(map((response) => response?.content));

  form = new UntypedFormGroup({
    searchText: new UntypedFormControl(),
  });

  private subs = new SubSink();
  firstChange = true;

  public partnersTableSource = new TableDataSource<
    MerchantSearchRequest,
    PageMerchantSearchResponse
  >(this.store);

  public totalPartnersResult$: Observable<number> = this.store
    .select(MerchantSelectors.selectPartnersResponse)
    .pipe(map((response) => response?.totalElements ?? 0));

  public pageSizeOptions = paginatorConfiguration.pageLargeSizeOptions;
  public pageSize: number = this.pageSizeOptions[0];

  private pageEvent: PageEvent = <PageEvent>{
    pageSize: this.pageSize,
  };

  constructor(
    private store: Store,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<PartnerSearchModalComponent>
  ) {
    this.pageEvent.pageSize = this.pageSize;
  }

  ngOnInit(): void {
    this.subs.sink = this.form
      .get('searchText')
      ?.valueChanges.pipe(skip(1), debounceTime(300), distinctUntilChanged())
      .subscribe((searchText) => {
        this.store.dispatch(
          MerchantActions.updatePartnersFilter.trigger({
            filter: { searchText: searchText ?? '' },
          })
        );
        this.store.dispatch(MerchantActions.searchPartners.trigger());
      });
  }

  ngAfterViewInit(): void {
    this.partnersTableSource.init(
      this.paginator,
      this.sort,
      undefined,
      ['checkboxes', 'companyName', 'brandName'],
      (filter, store: Store) =>
        store.dispatch(MerchantActions.searchPartners.trigger()),
      (filter, store: Store) =>
        store.dispatch(
          MerchantActions.updatePartnersFilter.trigger({ filter })
        ),
      (store: Store) => store.select(MerchantSelectors.selectPartnersResponse),
      (store: Store) =>
        store.select(MerchantSelectors.selectPartnersResponseLoading)
    );
    this.cdr.detectChanges();
  }

  checkboxLabel(id?: string): string {
    if (!id) {
      return `${
        this.partnersTableSource.isAllSelected() ? 'deselect' : 'select'
      } all`;
    } else return '';
  }

  selectPartners() {
    this.dialogRef.close(this.partnersTableSource.selection.selected);
  }

  ngOnDestroy(): void {
    this.store.dispatch(
      MerchantActions.updatePartnersFilter.trigger({
        filter: { page: 0, searchText: '' },
      })
    );
    this.subs.unsubscribe();
  }
}
